/**
 * three.js presentation layer: the pyramid, cell states, the camera, the piece
 * being dragged, the tray, and projecting the board back to screen coordinates
 * so aiming can be judged by eye. Knows nothing about rules — it is told what
 * to draw.
 */

import {
  ACESFilmicToneMapping,
  AdditiveBlending,
  AmbientLight,
  BufferGeometry,
  Color,
  DirectionalLight,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalNodeMaterial,
  MeshStandardMaterial,
  OrthographicCamera,
  PMREMGenerator,
  PerspectiveCamera,
  PostProcessing,
  Quaternion,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
  WebGPURenderer,
} from 'three/webgpu';
import { attribute, float, max, mix, pass, uv, vec4 } from 'three/tsl';
import { bloom } from 'three/examples/jsm/tsl/display/BloomNode.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

import { ApexBeam } from './beam.js';
import { buildGemFacetNormalMap, buildGemGeometry } from './gem.js';
import { GEM_TONES } from './palette.js';
import { BoardVFX } from './vfx.js';

import {
  APEX,
  BASE_VERTS,
  CELLS,
  CELL_COUNT,
  FACE_COUNT,
  cellCentroid,
  cellCorners,
  cellLatticeOrigin,
  faceBasis,
  faceNormal,
  latticeVertex,
  outlineEdges,
} from './geometry.js';
import { PIECES_BY_KEY, piecePolygons } from './pieces.js';

/** Centre of the pyramid's bounding box — the apex sits well above its centroid. */
const TARGET = new Vector3(0, (APEX[1] + BASE_VERTS[0][1]) / 2, 0);
const SNAP_STEP = (2 * Math.PI) / FACE_COUNT;

/**
 * Tilt range. The floor is level with the middle of the pyramid, so the
 * underside never comes into view; the ceiling still allows a good look down
 * over the top.
 */
const MIN_ELEVATION = 0.02;
const MAX_ELEVATION = 0.95;
const DEFAULT_ELEVATION = 0.3;

/**
 * A long lens. The pyramid turns in front of a camera that never moves, and a
 * narrow field of view keeps perspective from exaggerating whichever face
 * happens to be nearest as it comes round.
 */
const BASE_FOV = 26;
/** Fraction of the band the pyramid fills at its widest orientation. */
const FIT_FILL = 0.96;

/**
 * Views are yawed off-axis: a tetrahedron seen square on to a face shows that
 * face and nothing else, which reads as a flat triangle. Neighbouring faces
 * only appear past 30 degrees, so a settled view sits beyond that.
 */
const SNAP_YAW = 0.7;

/* ---------------------------------------------------------------- rotation */

/** Angular speed (rad/s) past which a flick pages to the next face. */
const FLING_SPEED = 1.9;
/** Below this a release just stops; between the two it coasts. */
const COAST_SPEED = 0.25;
/** Fraction of coasting speed retained per second. */
const COAST_FRICTION = 0.015;
/** A flick older than this is stale — the pointer had come to rest. */
const FLICK_WINDOW_MS = 90;
const PAGE_MS = 460;

const easeOutCubic = (t) => 1 - (1 - t) ** 3;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Frame-rate independent smoothing factor. */
const smoothing = (dt, rate) => 1 - Math.exp(-dt * rate);

/* ------------------------------------------------------------------ colour */

/**
 * Distance from a cell's centroid to its own edge, a cell edge being 1. Rim
 * corners are scaled about that centroid, so this is what turns a wanted gap in
 * board units into a scale factor. See `TUNING.cellInset`.
 */
const CELL_INCENTRE = 1 / (2 * Math.sqrt(3));

/** The pyramid is convex, so these four points bound its silhouette. */
const SILHOUETTE_CORNERS = [APEX, ...BASE_VERTS].map((v) => new Vector3(...v));

/** Turning spins about the upright axis; tilting leans the whole thing back. */
const SPIN_AXIS = new Vector3(0, 1, 0);
const TILT_AXIS = new Vector3(1, 0, 0);
const SPIN = new Quaternion();
const BOARD_TURN = new Quaternion();
const EYE = new Vector3();

/** Screen space owned by the HUD and the tray, kept clear of the pyramid. */
const HUD_PX = 70;
const TRAY_PX = 168;

/**
 * A regular tetrahedron maps onto itself every 120 degrees, so an orbit would
 * look like nothing happened. Each face gets its own tint, and the face being
 * faced stays at full strength while the others dim.
 *
 * The tints are near-black now that the stonework is polished metal, because
 * most of the work of telling the faces apart has moved to the reflections:
 * three faces at 120 degrees catch the environment quite differently, and that
 * reads as a turn far better than a hue ever did. What is left here is a hint
 * of hue to keep them from being identical when the reflection is quiet.
 */
const FACE_TINTS = [new Color(0x191c26), new Color(0x1e1a27), new Color(0x161e25)];
const UNFOCUSED_DIM = 0.62;

/**
 * Black metal reflects nothing. A metal tints its reflection by its own base
 * colour, so an albedo at zero gives a perfect mirror of pure darkness — the
 * pyramid comes out a flat silhouette. Dark charcoal reads as black against
 * this background while still having something for the environment to bounce
 * off.
 */
const METAL_ROUGHNESS = 0.24;
const METAL_METALNESS = 0.92;
/** The stonework is the mirror now, so it gets a real share of the probe. */
const METAL_ENV = 1.15;

/**
 * Every dimension of the stonework and its gold inlay, in board units where a
 * cell edge is exactly 1.
 *
 * One mutable object rather than a wall of constants, because these are what the
 * board's whole read hangs on and they can only really be judged against each
 * other, on screen, at speed. `tune.js` puts sliders on them and `retune()`
 * rebuilds from whatever they say — see `TUNING_SCHEMA` there for the ranges and
 * what each one does. Values good enough to keep get pasted back in here.
 */
export const TUNING = {
  /**
   * Cell scale about its own centroid, which decides where a socket's rim sits
   * against the cell's true boundary.
   *
   * Under 1 it stops short, leaving grout between cells, and any shortfall wider
   * than the wire lying in that grout shows as daylight between the black and the
   * gold. Over 1, as here, it runs past: neighbouring sockets overlap under the
   * wire, which is the one arrangement with no join to find at all.
   */
  cellInset: 1.05,

  /**
   * How far a socket's centre is pulled back behind the face it is cut into.
   *
   * A real cut: the rim stays level with the surface around it and the middle
   * drops away. What makes that possible is that the cells *are* the surface —
   * between them they close it, so the solid core can sit well behind. Back the
   * core out any less than this and it would stand in front of its own sockets
   * and fill them in.
   */
  socketDepth: 0.05,

  /**
   * How far a socket's sides drop from its outer ring, closing the space beneath.
   *
   * Off, because nothing here ever opens that space: the sockets close the surface
   * where they sit, and with the lifts below at 0 no cell ever rises out of it.
   * Lift a cell without sides and a slot opens down its side.
   */
  socketSkirt: 0,

  /** How far the whole cell floats off the shell. */
  liftBase: 0,
  /**
   * Extra lift for a filled cell, and for one showing where a drop would land.
   * Both flat here — those states carry on colour and glow alone, which keeps the
   * surface of the pyramid one unbroken plane per face.
   */
  liftFilled: 0,
  liftPreview: 0,

  /**
   * Half-thickness of the gold wire. A couple of percent of a cell is fine enough
   * to read as inlay rather than as fencing.
   */
  wireRadius: 0.0195,

  /**
   * How far the wire rides off the face — its own measurement, not the socket's.
   * At its own radius it sits on the surface like a rail; below that it beds into
   * the stone, and at 0 it is half buried with its underside open to the sockets.
   */
  wireLift: 0.023,

  /**
   * How far an end runs past its vertex, in multiples of the wire's own radius,
   * where there is another wire in that space to hide inside — which closes the
   * notch where two wires turn. At 0, as here, every end stops dead on its vertex:
   * with a wire this fine the notch is smaller than the swelling the overrun
   * leaves at each crossing. See `#resolveJoints`.
   */
  wireOverrun: 0,

  /**
   * Whether ends with nothing to hide inside are drawn together onto their shared
   * vertex — which mitres the apex, the corners of the base, and every wire that
   * runs into a crease. Off, they stop dead where they are and show a notch.
   */
  mitre: true,

  /**
   * Height of that meeting point, as a share of the wire's own standoff. At 1 the
   * mitre keeps every wire at the height it already runs at; lower pulls the last
   * stretch of each one down toward the shell.
   */
  mitreLift: 1,

  /** How far a stone sits off its face. At 0 its pavilion is buried in the solid. */
  gemLift: 0,
};

/**
 * How far the solid core is backed out behind the face planes: clear of the
 * deepest point of any socket cut into them, with a little to spare so the two
 * surfaces never meet. See `TUNING.socketDepth`.
 */
const coreSink = () => TUNING.socketDepth + 0.02;

/**
 * The gold is polished harder than the stonework it sits in (`metalness` 1, low
 * roughness), which is what separates the two materials: gold picks out the grid
 * against black.
 */
const WIRE_ROUGHNESS = 0.28;
const WIRE_ENV = 0.85;

const COLORS = {
  empty: new Color(0x232c5c),
  filled: new Color(0xdfbc7c),
  preview: new Color(0xffcf49),
  armed: new Color(0xfff0b8),
  clearing: new Color(0xfff4cf),
  ghost: new Color(0xffd76b),
  shell: new Color(0x15151b),
  base: new Color(0x0b0b0f),
  /** The wire between the cells. Gold albedo, because a metal tints its own
   *  reflection — this is the colour the environment comes back as. */
  wire: new Color(0xffc850),
  /** Border glow around a piece that has just landed. */
  glowPlace: new Color(0xffd76b),
  /** Border glow around a formation as its score is announced. */
  glowScore: new Color(0xfff3cd),
};

/**
 * Bloom. The threshold sits just under 1 so that only genuinely bright things
 * bloom — the additive particles, which are authored past 1 on purpose, and
 * the speculars off a stone's crown. The board itself stays where it is: lower
 * the threshold and the whole picture fogs over.
 */
const BLOOM_STRENGTH = 0.62;
const BLOOM_RADIUS = 0.62;
const BLOOM_THRESHOLD = 0.85;

/** How long a dropped piece takes to fly from the hand onto the face. */
const DROP_MS = 240;
/** Width of a border glow band, in board units. */
const GLOW_WIDTH = 0.05;
/** How far a border glow floats off its face, clear of the stones. */
const GLOW_LIFT = 0.06;
/** The drop outline is drawn heavier: it has to read from under the piece. */
const PREVIEW_GLOW_WIDTH = 1.5;
/** Breathing range of the drop outline, so it reads as live rather than painted. */
const PREVIEW_GLOW_PULSE = 0.22;

const CLEAR_MS = 460;

/** How fast the piece in hand chases its target. */
const GHOST_RATE = 18;

/**
 * Sway: the piece in hand hangs from the pointer and swings.
 *
 * A real driven pendulum rather than a tilt mapped off the pointer, because the
 * whole point is what happens when you *stop* — a mapped tilt snaps back to level
 * the instant the pointer halts, which reads as a rubber band. A pendulum carries
 * its momentum through level, overshoots, and settles, and that is what makes the
 * piece feel like it has weight.
 *
 * `STIFFNESS` is gravity over the length of the string. `DAMPING` is set to leave
 * it just underdamped — enough overshoot to notice one swing back, not enough to
 * wobble like jelly. `DRIVE` converts pointer speed in screen pixels per second
 * into the radians of lean it hangs at, and `MAX` caps a fast flick so a hard drag
 * cannot lay the piece on its side.
 *
 * Pitch — the tip away from you on vertical motion — is driven at a fraction of the
 * roll. Sideways swing is what a carried object plainly does; the same amount of
 * pitch just reads as the piece being loose.
 */
const SWAY_STIFFNESS = 95;
const SWAY_DAMPING = 11;
const SWAY_DRIVE = 0.00019;
const SWAY_PITCH_SHARE = 0.45;
const SWAY_MAX = 0.3;

/** Scratch for composing the sway, so the update loop allocates nothing. */
const SWAY_ROLL = new Quaternion();
const SWAY_PITCH = new Quaternion();
const SWAY_AXIS_Z = new Vector3(0, 0, 1);
const SWAY_AXIS_X = new Vector3(1, 0, 0);

/** The palette, as three.js colours. */
const TONES = GEM_TONES.map((hex) => new Color(hex));
const toneColor = (tone) => TONES[(tone ?? 0) % TONES.length];

/** One stone, shared by every cell and every piece in hand. */
const GEM_GEOMETRY = buildGemGeometry();
/** Turn a stone to sit in a downward-pointing cell. */
const FLIP_Z = new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), Math.PI);

/** Cell widths across a tray slot — shared, so piece sizes stay comparable. */
const TRAY_EXTENT = 1.85;
/** Tray stones are tilted a little so their crowns catch the light. */
const TRAY_TILT = 0.16;

/**
 * Reflection character, spread across the facets.
 *
 * `facetVariance` (built in `gem.js`) carries two hashed values per facet.
 * The first picks a polish between these bounds, so some facets come back as
 * near-mirrors and others scatter — the range is what stops the stone reading
 * as one glossy solid with brightness painted on. The second picks a thin-film
 * thickness, which is the stone's fire: each facet flashes a different hue as
 * the board turns, the way a cut stone disperses light.
 */
const GEM_ROUGHNESS = [0.03, 0.26];
const GEM_FIRE_NM = [180, 640];

/**
 * How far a facet may lean off its true plane, and how hard that lean is
 * applied. Small on purpose: this is meant to stop coplanar facets mirroring
 * the same thing, not to make the stone look dented.
 */
const GEM_FACET_TILT_DEG = 9;
const GEM_NORMAL_SCALE = 1;

/** One texture, shared by every stone — it is addressed by facet, not by tone. */
const GEM_NORMAL_MAP = buildGemFacetNormalMap(
  GEM_GEOMETRY.userData.facetGrid,
  GEM_FACET_TILT_DEG,
);

function gemMaterial() {
  const material = new MeshPhysicalNodeMaterial({
    color: COLORS.filled,
    vertexColors: true,
    metalness: 0,
    normalMap: GEM_NORMAL_MAP,
    normalScale: new Vector2(GEM_NORMAL_SCALE, GEM_NORMAL_SCALE),
    // A full clearcoat lays a white sheen over everything, which is most of
    // what made the stones read pastel. Enough for a wet gleam, no more.
    clearcoat: 0.45,
    clearcoatRoughness: 0.08,
    envMapIntensity: 0.85,
    emissive: new Color(0x000000),
    iridescence: 0.35,
    iridescenceIOR: 1.8,
  });

  // Roughness and film thickness come from the geometry rather than from
  // constants, which is the whole difference between a faceted shape and
  // something that reads as cut stone. Only these two slots are overridden, so
  // `color` and `emissive` stay ordinary properties for the cell states to set.
  const facet = attribute('facetVariance');
  material.roughnessNode = mix(float(GEM_ROUGHNESS[0]), float(GEM_ROUGHNESS[1]), facet.x);
  material.iridescenceThicknessNode = mix(float(GEM_FIRE_NM[0]), float(GEM_FIRE_NM[1]), facet.y);
  return material;
}

export class PyramidView {
  constructor(canvas) {
    this.canvas = canvas;

    // WebGPU, because the particle runtime is a TSL/NodeMaterial library and
    // will not run on the classic renderer. It picks the WebGL2 backend by
    // itself wherever WebGPU is missing (Safari, iOS), so this is not a
    // narrowing of what the game runs on — see `init`, which has to be awaited
    // before the first frame either way.
    this.renderer = new WebGPURenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setClearColor(0x000000, 0);
    // Stones throw very bright speculars; roll them off rather than clipping.
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.82;

    this.scene = new Scene();
    this.camera = new PerspectiveCamera(BASE_FOV, 1, 0.1, 100);

    this.azimuth = 0;
    this.elevation = DEFAULT_ELEVATION;

    /** Free-spin speed in rad/s, and the page-to-a-face tween. */
    this.spin = 0;
    this.page = null;
    this.dragging = false;
    this.lastFrame = 0;
    this.flickSpeed = 0;
    this.flickAt = 0;

    // The camera never moves. Turning the view spins the pyramid instead, about
    // a pivot at the centre of its bounding box so it turns in place.
    this.pivot = new Group();
    this.pivot.position.copy(TARGET);
    this.scene.add(this.pivot);

    this.pyramid = new Group();
    this.pyramid.position.set(-TARGET.x, -TARGET.y, -TARGET.z);
    this.pivot.add(this.pyramid);

    this.raycaster = new Raycaster();
    this.pointer = new Vector2();

    /** cell id -> flat socket mesh */
    this.cellMeshes = [];
    /** cell id -> the stone sitting in it */
    this.gemMeshes = [];
    /** cell id -> 'empty' | 'filled' */
    this.cellState = Array(CELL_COUNT).fill('empty');
    /** cell id -> palette index of the stone sitting there */
    this.cellTone = Array(CELL_COUNT).fill(0);
    /** cell id -> timestamp when its clear animation began */
    this.clearing = new Map();
    /** cells currently shown as a drop preview */
    this.preview = new Set();
    /** already-filled cells that the preview would clear */
    this.armed = new Set();

    this.faceCentroids = Array.from({ length: FACE_COUNT }, (_, face) => {
      const b = BASE_VERTS[face];
      const c = BASE_VERTS[(face + 1) % FACE_COUNT];
      return [
        (APEX[0] + b[0] + c[0]) / 3,
        (APEX[1] + b[1] + c[1]) / 3,
        (APEX[2] + b[2] + c[2]) / 3,
      ];
    });
    /**
     * Which faces are turned toward the camera, by face index. Part of the
     * aiming contract: every visible face is a live drop target, so this is
     * what `resolveTarget` filters candidates by. Kept current every frame by
     * `#updateFacing`.
     */
    this.visibleFaces = Array(FACE_COUNT).fill(true);

    // The piece in hand lives in its own scene, drawn in a second pass over a
    // cleared depth buffer, so it is always in front of the pyramid — even when
    // it is floating loose rather than snapped onto a face.
    this.ghostScene = new Scene();
    this.ghostPivot = new Group();
    this.ghostScene.add(this.ghostPivot);
    this.ghostBoard = new Group();
    this.ghostBoard.position.copy(this.pyramid.position);
    this.ghostPivot.add(this.ghostBoard);

    this.#buildTray();
    this.#buildLights();
    this.#buildCore();
    this.#buildBase();
    this.#buildCells();
    this.#buildEdges();
    this.#buildGhost();

    this.resize();
  }

  /**
   * Everything that needs a live GPU device, which on WebGPU means everything
   * after `renderer.init()` has resolved: the reflection probe, and the
   * particle system's node materials.
   *
   * Kept out of the constructor rather than folded into the first frame so
   * that a caller can await it — otherwise the opening frames render the
   * stones with no environment map and they pop as it arrives.
   */
  async init() {
    await this.renderer.init();
    this.#buildEnvironment();
    this.vfx = new BoardVFX(this.pyramid);
    // A child of the pyramid group: the column stands on the board's own axis
    // and tilts with it, which is what keeps its buried foot actually buried —
    // and lets the stonework occlude everything below the tip. Built before the
    // post chain, which reads its impact ring straight out of it.
    this.beam = new ApexBeam(this.pyramid);
    this.beam.resize(this.viewW, this.viewH);
    this.#buildPostProcessing();
    return this;
  }

  /**
   * Bloom over the whole picture.
   *
   * The three passes were three `render` calls over a cleared depth buffer,
   * which is a compositing trick that post-processing cannot see inside: it
   * would bloom whatever happened to be in the frame buffer last. So each
   * scene becomes a `pass` node with its own target, and the layering that
   * `clearDepth` used to do is done explicitly here, by alpha — the piece in
   * hand over the board, the tray over both. Depth still sorts correctly
   * *within* each pass, which is all it was ever doing.
   *
   * Passes render linear and un-tone-mapped into half-float targets, so the
   * bright end of a stone's specular and the additive particles keep values
   * well above 1 for bloom to find. Tone mapping happens once, at the end, on
   * the composite — which is the whole reason this reads as light rather than
   * as a blur: `outputColorTransform` rolls the glow off with the highlight it
   * came from instead of clipping both to white.
   */
  #buildPostProcessing() {
    const board = pass(this.scene, this.camera);
    const ghost = pass(this.ghostScene, this.camera);
    const tray = pass(this.trayScene, this.trayCamera);

    // The impact ring, bending the board layer only.
    //
    // Only that layer, because the other two are things held in front of the
    // board rather than part of the scene — a shockwave off the pyramid warping
    // the tray would read as the screen itself being made of jelly.
    //
    // Three samples rather than one so the ring splits colour as it passes: red
    // is pushed a little further than green and blue a little less, which is what
    // a lens does and what stops the warp reading as a smear. Away from the ring
    // the offset is exactly zero and all three land on the same texel.
    const base = uv();
    const offset = this.beam.shockOffset(base);
    const boardTex = board.getTextureNode();
    const centre = boardTex.sample(base.add(offset).clamp(0, 1)).toVar();
    const warped = vec4(
      boardTex.sample(base.add(offset.mul(1.3)).clamp(0, 1)).r,
      centre.g,
      boardTex.sample(base.add(offset.mul(0.7)).clamp(0, 1)).b,
      centre.a,
    );

    // `over`: the top layer where it is opaque, the one below where it is not.
    // Alpha is kept as the larger of the two so the canvas stays transparent
    // wherever nothing was drawn and the page's own background still shows.
    const over = (under, top) => vec4(mix(under.rgb, top.rgb, top.a), max(under.a, top.a));
    const layered = over(over(warped, ghost), tray);

    this.post = new PostProcessing(this.renderer);
    this.post.outputNode = layered.add(bloom(layered, BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD));
  }

  #buildEnvironment() {
    // Reflections do most of the work on the stones.
    const pmrem = new PMREMGenerator(this.renderer);
    const environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();
    this.scene.environment = environment;
    this.ghostScene.environment = environment;
    this.trayScene.environment = environment;
  }

  #buildLights() {
    // The board and the piece in hand share a rig, so a carried stone looks
    // like the stones already set into the pyramid.
    for (const scene of [this.scene, this.ghostScene]) {
      // Little ambient: it lifts the shadowed facets and flattens every stone
      // toward pastel. Contrast comes from a hard key against a cool rim.
      scene.add(new AmbientLight(0x6d81c4, 0.24));

      // Off to the side and a touch behind, rather than over the camera's
      // shoulder.
      //
      // The camera looks down -Z, so a face turned square-on has both its
      // normal and the view vector at +Z, and the specular peak sits on the
      // half-vector between lamp and eye. From the old position that landed
      // 28° off the view axis — close enough that a square-on face mirrored
      // the lamp back down the lens. That was survivable while the stonework
      // was matte, but it is polished metal now, whose every facet returns the
      // lamp at full strength, and the gold wire's prisms present facets at
      // every angle, so something was always aimed straight back. Bloom then
      // took whatever glinted and spread it over the frame.
      //
      // From here the peak is 48° off-axis, which no flat face and no socket
      // wall can reach. Still forward enough to rake the stones' crowns, which
      // is the job it was placed for.
      const key = new DirectionalLight(0xfff4dc, 2.35);
      key.position.set(6.2, 5.6, -1);
      scene.add(key);

      // Cool, but no longer *blue*. A metal has no diffuse term, so every bit
      // of this light comes back as specular wearing its own colour — and the
      // stonework is metal now. At the old saturation whole faces turned blue
      // wherever their socket walls happened to face this way, which is the one
      // thing the black stonework was meant to stop.
      const rim = new DirectionalLight(0x93a6d0, 0.72);
      rim.position.set(-4, 1.5, -3);
      scene.add(rim);
    }

    // The tray needs a rig of its own. Its stones face the camera head on, so
    // the board's raking key only skims their crowns — but a light brought
    // round to the front instead grazes the crown facets, which all slope away
    // from the axis, and the stones go dull.
    //
    // The catch is parity: a downward stone is an upward one turned 180
    // degrees, so a single off-axis key that models one leaves the other in
    // shadow. Two keys mirrored through the crown axis are the way out —
    // together they light both parities identically while still meeting the
    // crown facets at an angle that lights them.
    // 1.7 rather than the board's 2.5: two keys both reach a tray stone, and
    // over-lighting one costs colour rather than gaining brightness — the
    // facets clip to white and the tone drains out of them. Measured against
    // the board, this lands the tray within 0.02 saturation of it.
    for (const sign of [1, -1]) {
      const key = new DirectionalLight(0xfff2d8, 1.7);
      key.position.set(1.5 * sign, 1.7 * sign, 2.2);
      this.trayScene.add(key);
    }

    // Head-on fill, enough to keep the tables from reading as flat cutouts and
    // no more — every bit of flat front light washes the colour out of a stone.
    const trayFill = new DirectionalLight(0xa9bdf5, 0.4);
    trayFill.position.set(0, 0, 3);
    this.trayScene.add(trayFill);
    this.trayScene.add(new AmbientLight(0x8ea0d8, 0.2));
  }

  /** Sit a stone in a cell: crown along the face's outward normal. */
  #placeGem(mesh, id) {
    const cell = CELLS[id];
    const basis = faceBasis(cell.face);
    const centroid = cellCentroid(id);
    const normal = basis.normal;

    const right = new Vector3(...basis.right);
    const up = new Vector3(...basis.down).negate();
    const out = new Vector3().crossVectors(right, up);

    mesh.quaternion.setFromRotationMatrix(new Matrix4().makeBasis(right, up, out));
    // A downward cell is the same triangle turned half a turn.
    if (!cell.up) mesh.quaternion.multiply(FLIP_Z);

    mesh.position.set(
      centroid[0] + normal[0] * TUNING.gemLift,
      centroid[1] + normal[1] * TUNING.gemLift,
      centroid[2] + normal[2] * TUNING.gemLift,
    );
  }

  /**
   * The solid inside the playable skin, and the plate it stands on.
   *
   * The cells are the skin now, and between them they close it, so the core is
   * not what anyone looks at: it is what stops a lifted cell — or the flare of a
   * clearing one — showing a slot straight through to the far side of the
   * pyramid. It sits `coreSink()` behind the three playing faces, because a
   * socket is cut back behind them and a core any further out would stand in
   * front of its own sockets and fill them in.
   *
   * Only the three playing faces move. The scale is about the centre of the base,
   * which leaves the base plane where it is and so keeps the plate flush against
   * it; scaling about the tetrahedron's own centroid would lift the base off the
   * plate and open a slit right around the bottom edge. Drawing the base triangle
   * in laterally tilts no plane, so each side plane moves in by that distance
   * times how much of its normal is horizontal — which is what turns the sink
   * into a scale factor. The base triangle's incentre-to-edge distance is the z
   * of its first vertex.
   */
  #buildCore() {
    const [nx, , nz] = faceNormal(0);
    const lateral = coreSink() / Math.hypot(nx, nz);
    const shrink = 1 - lateral / BASE_VERTS[0][2];
    const baseY = BASE_VERTS[0][1];
    const verts = [];
    const push = (t) => {
      for (const p of t) {
        verts.push(p[0] * shrink, baseY + (p[1] - baseY) * shrink, p[2] * shrink);
      }
    };

    for (let face = 0; face < FACE_COUNT; face += 1) {
      push([APEX, BASE_VERTS[face], BASE_VERTS[(face + 1) % FACE_COUNT]]);
    }
    push([BASE_VERTS[0], BASE_VERTS[2], BASE_VERTS[1]]);

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(verts, 3));
    geometry.computeVertexNormals();

    this.coreMesh = new Mesh(
      geometry,
      new MeshStandardMaterial({
        color: COLORS.shell,
        roughness: METAL_ROUGHNESS,
        metalness: METAL_METALNESS,
        envMapIntensity: METAL_ENV,
        side: DoubleSide, // the base triangle is wound for the outside
      }),
    );
    this.pyramid.add(this.coreMesh);
  }

  /** The plate the pyramid stands on. Takes no tuning. */
  #buildBase() {
    const baseGeom = new BufferGeometry();
    const basePts = [];
    for (const p of [BASE_VERTS[0], BASE_VERTS[2], BASE_VERTS[1]]) {
      basePts.push(p[0], p[1] - 0.002, p[2]);
    }
    baseGeom.setAttribute('position', new Float32BufferAttribute(basePts, 3));
    baseGeom.computeVertexNormals();
    this.pyramid.add(
      new Mesh(
        baseGeom,
        new MeshStandardMaterial({
        color: COLORS.base,
        roughness: 0.6,
        metalness: 0.8,
        envMapIntensity: 0.5,
      }),
      ),
    );
  }

  /**
   * Throw away the stonework and its inlay and build them again from whatever
   * `TUNING` now says. For the slider panel — nothing in the game itself calls
   * this. The base plate is left alone, being the one part that takes no tuning.
   *
   * Cell *state* lives in plain arrays rather than on the meshes, and both
   * visibility and colour are set every frame, so a rebuilt board picks up the
   * game it was in the middle of by itself.
   */
  retune() {
    this.pyramid.remove(this.coreMesh);
    this.coreMesh.geometry.dispose();
    this.coreMesh.material.dispose();
    this.#buildCore();

    for (const mesh of this.cellMeshes) {
      this.pyramid.remove(mesh);
      mesh.geometry.dispose();
      mesh.material.dispose();
    }
    for (const gem of this.gemMeshes) {
      this.pyramid.remove(gem);
      // The geometry is shared by every stone on the board — not ours to drop.
      gem.material.dispose();
    }
    for (const wire of [...this.faceEdges, ...this.seamEdges]) {
      this.pyramid.remove(wire);
      wire.geometry.dispose();
      wire.material.dispose();
    }
    this.cellMeshes = [];
    this.gemMeshes = [];
    this.#buildCells();
    this.#buildEdges();
  }

  #buildCells() {
    for (let id = 0; id < CELL_COUNT; id += 1) {
      const corners = cellCorners(id);
      const centroid = cellCentroid(id);
      const normal = faceNormal(CELLS[id].face);

      // A cell is a piece of the pyramid's own surface with a dish cut into it,
      // rather than a tile laid on top of one. Three rings, all local to the
      // cell's own centre so the whole thing can scale and lift:
      //
      //   edge   the cell's true boundary, flat on the face plane.
      //   rim    where the ground starts to fall away — the boundary scaled by
      //          `cellInset`. Inside the edge, the flat frame between the two is
      //          what makes the cells tile the face so there is no gap to see
      //          through. Outside it, they overlap their neighbours instead,
      //          which closes the surface just as well and is the reason a scale
      //          over 1 is allowed at all.
      //   floor  one point in the middle, pulled back behind the face by
      //          `socketDepth`. The cut goes *into* the pyramid.
      const edge = corners.map((p) => [
        p[0] - centroid[0],
        p[1] - centroid[1],
        p[2] - centroid[2],
      ]);
      const rim = edge.map((p) => [
        p[0] * TUNING.cellInset,
        p[1] * TUNING.cellInset,
        p[2] * TUNING.cellInset,
      ]);
      const floor = [
        -normal[0] * TUNING.socketDepth,
        -normal[1] * TUNING.socketDepth,
        -normal[2] * TUNING.socketDepth,
      ];
      // Whichever ring is the outer one — which is what the sides have to hang
      // from, and what the frame is measured against.
      const outer = TUNING.cellInset > 1 ? rim : edge;
      // Where the sides land, behind the face. See `TUNING.socketSkirt`.
      const foot = outer.map((p) => [
        p[0] - normal[0] * TUNING.socketSkirt,
        p[1] - normal[1] * TUNING.socketSkirt,
        p[2] - normal[2] * TUNING.socketSkirt,
      ]);

      // `cellCorners` winds counter-clockwise seen from outside, so taking each
      // ring in that order and stepping inward or backward from it gives faces
      // that look out of the pyramid. The floor is behind the rim, which makes
      // (rim, next rim, floor) face up and in — the inside of a dish. Reverse any
      // of these and the walls light from behind.
      const local = [];
      for (let i = 0; i < 3; i += 1) {
        const j = (i + 1) % 3;
        // The flat frame in from the cell's boundary, where there is one. With the
        // rim outside the boundary there is nothing to fill, and these two would
        // wind inside out.
        if (TUNING.cellInset < 1) {
          local.push(...edge[i], ...edge[j], ...rim[i]);
          local.push(...edge[j], ...rim[j], ...rim[i]);
        }
        // The dish.
        local.push(...rim[i], ...rim[j], ...floor);
        // The sides, dropping back from the outer ring — nothing to draw if they
        // have no depth, which is two triangles a cell not spent.
        if (TUNING.socketSkirt > 0) {
          local.push(...outer[i], ...foot[i], ...foot[j]);
          local.push(...outer[i], ...foot[j], ...outer[j]);
        }
      }

      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new Float32BufferAttribute(local, 3));
      // Non-indexed, so each wall keeps its own normal and the three catch the
      // environment at three different angles — which is what makes the cut
      // read as a cut rather than as a shaded triangle.
      geometry.computeVertexNormals();

      const mesh = new Mesh(
        geometry,
        new MeshStandardMaterial({
          color: COLORS.empty,
          roughness: METAL_ROUGHNESS,
          metalness: METAL_METALNESS,
          envMapIntensity: METAL_ENV,
          emissive: new Color(0x000000),
        }),
      );
      mesh.userData.cellId = id;
      mesh.userData.centroid = centroid;
      mesh.userData.normal = normal;
      mesh.position.set(centroid[0], centroid[1], centroid[2]);

      this.cellMeshes[id] = mesh;
      this.pyramid.add(mesh);

      // The stone that fills this cell, hidden until something is placed here.
      const gem = new Mesh(GEM_GEOMETRY, gemMaterial());
      gem.visible = false;
      this.#placeGem(gem, id);
      this.gemMeshes[id] = gem;
      this.pyramid.add(gem);
    }
  }

  /**
   * Gold wire laid in the grout between the cells, so the grid stays legible
   * when the board is empty. Grouped per face so hidden faces switch off with
   * their cells.
   *
   * Real geometry rather than `LineSegments`, because a line primitive is one
   * pixel wide whatever the distance, takes no light and cannot reflect — set
   * into black metal it reads as a drawn-on diagram. A prism has facets, so it
   * catches the key light as a travelling highlight as the pyramid turns, picks
   * up the environment like the stonework around it, and is bright enough at
   * its specular peak to cross the bloom threshold and actually glint.
   *
   * The cross-section is a diamond — four flat sides around the edge — which is
   * the cheapest section that still gives a moving highlight. A square section
   * would present a flat top face and read as a ribbon.
   */
  #buildEdges() {
    // Every cell edge, and which faces claim it. Within a face, neighbouring
    // cells share edges; between faces, the three seams running apex to base
    // are claimed twice over. Either way two prisms in one place fight for the
    // same pixels and come out double-bright, so each edge is built once.
    const edges = new Map();
    for (let id = 0; id < CELL_COUNT; id += 1) {
      const { face } = CELLS[id];
      const corners = cellCorners(id);
      for (let i = 0; i < 3; i += 1) {
        const a = corners[i];
        const b = corners[(i + 1) % 3];
        const key = [a, b]
          .map((p) => p.map((v) => v.toFixed(4)).join(','))
          .sort()
          .join('|');
        const seen = edges.get(key);
        if (seen) seen.faces.add(face);
        else edges.set(key, { a, b, faces: new Set([face]) });
      }
    }

    // Edges belonging to one face go in that face's wire and hide with it.
    // A seam belongs to two, so it gets its own group — it has to stay up while
    // either of its faces is turned toward us, and it is laid along the crease
    // between them rather than flat on either one.
    const perFace = Array.from({ length: FACE_COUNT }, () => []);
    const seams = new Map();
    for (const edge of edges.values()) {
      const faces = [...edge.faces].sort((x, y) => x - y);
      if (faces.length === 1) {
        perFace[faces[0]].push({ ...edge, normal: faceNormal(faces[0]) });
        continue;
      }
      const key = faces.join('-');
      if (!seams.has(key)) seams.set(key, { faces, edges: [] });
      // On the crease, the outward direction is the bisector of the two faces.
      const [n1, n2] = faces.map(faceNormal);
      const bisector = new Vector3(n1[0] + n2[0], n1[1] + n2[1], n1[2] + n2[2]).normalize();
      seams.get(key).edges.push({ ...edge, normal: bisector.toArray() });
    }

    // Every wire in one list, because a joint is a property of the vertex rather
    // than of any one group: a row wire on one face ends against a seam, and only
    // by looking at all of them at once can either know how to finish.
    const seamList = [...seams.values()];
    this.#resolveJoints([...perFace.flat(), ...seamList.flatMap((s) => s.edges)]);

    this.faceEdges = perFace.map((list) => {
      const wire = this.#wireMesh(list);
      this.pyramid.add(wire);
      return wire;
    });

    this.seamEdges = seamList.map(({ faces, edges: list }) => {
      const wire = this.#wireMesh(list);
      wire.userData.faces = faces;
      this.pyramid.add(wire);
      return wire;
    });
  }

  /**
   * Work out how each wire should finish at each of its two ends, by looking at
   * what else arrives at the same vertex. Sets `endA`/`endB` — the wire's axis,
   * already lifted off the face — and `extA`/`extB`, whether that end runs on
   * past its vertex.
   *
   * Running every end past its corner by the wire's own radius closes the notch
   * where two wires turn, which is why it was worth doing — but it only works
   * where there is another wire in that space to hide inside. Where there isn't,
   * the same overrun is a nub hanging in the air: at the apex, at the corners of
   * the base, and wherever a wire runs into the crease between two faces, since
   * carrying straight on across a crease leaves the far face behind and pokes
   * out over it.
   *
   * So an end only overruns when another wire at that vertex lies in the same
   * plane and heads back the way this one came. The rest are *free* ends, and
   * where several meet they are drawn together onto their common midpoint —
   * which mitres the corner: the three seams close the apex into a point instead
   * of crossing past it, and two rows meeting at a crease meet under the seam
   * wire that covers them.
   */
  #resolveJoints(wires) {
    /** vertex position -> the ends that arrive there */
    const arrivals = new Map();

    for (const wire of wires) {
      const normal = new Vector3(...wire.normal);
      wire.normal3 = normal;
      wire.endA = new Vector3(...wire.a).addScaledVector(normal, TUNING.wireLift);
      wire.endB = new Vector3(...wire.b).addScaledVector(normal, TUNING.wireLift);
      wire.extA = false;
      wire.extB = false;

      // `out` is the way this end would overrun; `into` is the way its own wire
      // runs off from the vertex. One end covers another when it runs back into
      // the space that other end wants to overrun into.
      const along = new Vector3().subVectors(wire.endB, wire.endA).normalize();
      for (const at of ['A', 'B']) {
        const into = at === 'A' ? along.clone() : along.clone().negate();
        const vertex = at === 'A' ? wire.a : wire.b;
        const key = vertex.map((v) => v.toFixed(4)).join(',');
        const list = arrivals.get(key);
        const end = { wire, at, vertex, normal, into, out: into.clone().negate() };
        if (list) list.push(end);
        else arrivals.set(key, [end]);
      }
    }

    for (const ends of arrivals.values()) {
      const free = [];
      for (const end of ends) {
        const covered = ends.some(
          (other) =>
            other !== end &&
            other.normal.dot(end.normal) > 0.999 &&
            other.into.dot(end.out) > 0.1,
        );
        if (covered) end.wire[end.at === 'A' ? 'extA' : 'extB'] = true;
        else free.push(end);
      }

      if (free.length < 2 || !TUNING.mitre) continue;

      // They meet over their vertex at the one height they all agree on: their
      // shared standoff, along the average of the ways they each face. Averaging
      // the end *points* instead would work too, but it shortens the standoff and
      // so dips the last stretch of every wire toward the shell.
      const meet = new Vector3();
      for (const end of free) meet.add(end.normal);
      meet.normalize().multiplyScalar(TUNING.wireLift * TUNING.mitreLift);
      meet.x += free[0].vertex[0];
      meet.y += free[0].vertex[1];
      meet.z += free[0].vertex[2];
      for (const end of free) end.wire[end.at === 'A' ? 'endA' : 'endB'].copy(meet);
    }
  }

  /** One mesh of gold prisms, one per edge. See `#buildEdges` for the shape. */
  #wireMesh(list) {
    const A = new Vector3();
    const B = new Vector3();
    const along = new Vector3();
    const across = new Vector3();
    const up = new Vector3();
    const normal = new Vector3();

    const positions = [];
    const tri = (p, q, r) => positions.push(...p.toArray(), ...q.toArray(), ...r.toArray());

    for (const edge of list) {
      // Ends, lift and overruns are all settled by `#resolveJoints`, which is the
      // only place that can see what else meets this wire at either end.
      normal.copy(edge.normal3);
      A.copy(edge.endA);
      B.copy(edge.endB);
      along.subVectors(B, A).normalize();
      const overrun = TUNING.wireRadius * TUNING.wireOverrun;
      if (edge.extA) A.addScaledVector(along, -overrun);
      if (edge.extB) B.addScaledVector(along, overrun);

      // (across, up, along) is right-handed, which is what makes the winding
      // below come out facing away from the wire's axis.
      across.crossVectors(normal, along).setLength(TUNING.wireRadius);
      up.copy(normal).setLength(TUNING.wireRadius);
      const ring = [across.clone(), up.clone(), across.clone().negate(), up.clone().negate()];

      for (let i = 0; i < 4; i += 1) {
        const c1 = ring[i];
        const c2 = ring[(i + 1) % 4];
        tri(A.clone().add(c1), A.clone().add(c2), B.clone().add(c2));
        tri(A.clone().add(c1), B.clone().add(c2), B.clone().add(c1));
      }

      // Caps. Hidden inside the neighbouring wire at every junction, but the
      // outer boundary ends in open air and would show a hollow tube.
      tri(A.clone().add(ring[0]), A.clone().add(ring[3]), A.clone().add(ring[2]));
      tri(A.clone().add(ring[0]), A.clone().add(ring[2]), A.clone().add(ring[1]));
      tri(B.clone().add(ring[0]), B.clone().add(ring[1]), B.clone().add(ring[2]));
      tri(B.clone().add(ring[0]), B.clone().add(ring[2]), B.clone().add(ring[3]));
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    // Non-indexed, so every side of the prism keeps its own flat normal and the
    // four catch the light separately — a smoothed tube is just a dull cylinder.
    geometry.computeVertexNormals();

    return new Mesh(
      geometry,
      new MeshStandardMaterial({
        color: COLORS.wire,
        roughness: WIRE_ROUGHNESS,
        metalness: 1,
        envMapIntensity: WIRE_ENV,
      }),
    );
  }

  /* -------------------------------------------------------------------- tray */

  /**
   * Draw the tray's pieces as the stones they are, rather than flat triangles.
   *
   * They share the canvas rather than living in the DOM: an orthographic camera
   * in pixel coordinates lets each piece be parked exactly over its slot, so
   * the (now invisible) slot divs stay the hit targets and the layout stays
   * CSS's job. Scaling is shared across slots, so a one-stone piece looks one
   * stone big beside a three.
   *
   * @param {{key: string, tone: number}[]} entries
   * @param {number|null} heldSlot slot currently in hand — shown empty
   * @param {DOMRect[]} rects slot rectangles, relative to the canvas
   */
  syncTray(entries, heldSlot, rects) {
    for (const group of [...this.trayRoot.children]) {
      this.trayRoot.remove(group);
      for (const gem of group.children) gem.material.dispose();
    }
    if (!rects?.length) return;

    entries.forEach((entry, slot) => {
      const rect = rects[slot];
      if (!entry || slot === heldSlot || !rect) return;

      const group = new Group();
      const polys = piecePolygons(PIECES_BY_KEY.get(entry.key));

      // Centre the piece on its own bounding box so slots read evenly.
      const xs = polys.flatMap((p) => p.points.map((q) => q[0]));
      const ys = polys.flatMap((p) => p.points.map((q) => q[1]));
      const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
      const cy = (Math.min(...ys) + Math.max(...ys)) / 2;

      for (const poly of polys) {
        const px = poly.points.reduce((sum, q) => sum + q[0], 0) / poly.points.length;
        const py = poly.points.reduce((sum, q) => sum + q[1], 0) / poly.points.length;

        const gem = new Mesh(GEM_GEOMETRY, gemMaterial());
        gem.material.color.copy(toneColor(entry.tone));
        // A touch of self-light: tray stones sit away from the key light.
        gem.material.emissive.copy(toneColor(entry.tone)).multiplyScalar(0.2);
        gem.position.set(px - cx, -(py - cy), 0);
        if (!poly.up) gem.quaternion.copy(FLIP_Z);
        group.add(gem);
      }

      const size = Math.min(rect.width, rect.height);
      group.scale.setScalar(size / TRAY_EXTENT);
      group.rotation.x = TRAY_TILT;
      // Pixel coordinates, with y measured up from the bottom of the canvas.
      group.position.set(
        rect.left + rect.width / 2,
        this.viewH - (rect.top + rect.height / 2),
        0,
      );
      this.trayRoot.add(group);
    });
  }

  #buildTray() {
    this.trayScene = new Scene();
    this.trayRoot = new Group();
    this.trayScene.add(this.trayRoot);
    this.trayCamera = new OrthographicCamera(0, 1, 1, 0, -500, 500);
    this.trayCamera.position.set(0, 0, 100);
  }

  /** The piece the player is dragging, floating above the surface. */
  #buildGhost() {
    this.ghost = new Group();
    this.ghost.visible = false;
    // Rides in the mirrored board, so its placement maths stays in board
    // coordinates while the second render pass keeps it in front.
    this.ghostBoard.add(this.ghost);

    /** Where the ghost is easing toward. */
    this.ghostTargetPos = new Vector3();
    this.ghostTargetQuat = new Quaternion();
    this.ghostPlaced = false;

    /**
     * Sway state: where the piece is hanging, and how fast it is swinging.
     *
     * `x` is roll (from sideways motion), `y` is pitch (from vertical). The pointer
     * is tracked in *screen* pixels and differenced per frame rather than per
     * pointer event: events arrive in bursts and at whatever rate the browser feels
     * like, so dividing one event's delta by one event's dt gives a velocity that
     * spikes and stalls. A fixed frame step gives a speed the spring can follow.
     */
    this.sway = new Vector2();
    this.swayVel = new Vector2();
    this.ghostPointer = new Vector2();
    this.ghostPointerLast = new Vector2();

    /**
     * The ghost's orientation *before* the swing is added.
     *
     * Kept apart from `ghost.quaternion` because the sway is composed onto it every
     * frame. Swinging the displayed rotation in place would feed each frame's lean
     * back in as the next frame's starting point, and the piece would wind itself up
     * instead of hanging.
     */
    this.ghostQuat = new Quaternion();

    /**
     * Offset from the ghost group's origin to the piece's middle, in the
     * piece's own 2D units. The stones are recentred on the piece so that the
     * group's origin *is* its centre — which is what rides under the pointer
     * and what chooses the target — so seating it on a face has to add this
     * back to reach the anchor cell's lattice origin.
     */
    this.ghostCentre = new Vector2();

    /** In-flight drop: the piece easing from the hand onto the face. */
    this.drop = null;

    /** Cells whose stones are withheld while a piece flies down onto them. */
    this.withheld = new Set();

    /**
     * Cells still drawn as filled although the rules have already cleared
     * them — the scoring sequence shows them before they go.
     * @type {Map<number, number>} cell id -> palette index
     */
    this.heldStones = new Map();

    /** Border glows currently on the board, from placing and from scoring. */
    this.glows = new Group();
    this.pyramid.add(this.glows);

    /**
     * The outline round where a carried piece would land. Kept apart from
     * `glows` so clearing the scoring glows never disturbs it, and rebuilt only
     * when the target changes — keyed on the cells and tone.
     */
    this.previewGlows = new Group();
    this.pyramid.add(this.previewGlows);
    this.previewKey = '';
    this.previewTone = null;
  }

  /**
   * Start (or stop) dragging a piece.
   *
   * The piece in hand is built from the same stones that will land: one gem per
   * cell, laid out on the piece's own 2D artwork — the very polygons the tray
   * draws — so carrying it and dropping it look like the same object.
   */
  setHeldPiece(piece, tone = 0) {
    // A drop in flight owns the ghost; abandoning it would strand its cells in
    // the withheld set and leave the caller's promise hanging.
    if (this.drop) {
      for (const id of this.drop.cells) this.withheld.delete(id);
      const { resolve } = this.drop;
      this.drop = null;
      resolve();
    }

    this.heldPiece = piece ?? null;
    // Kept so the landing dust can wear the piece's colour: by the time the
    // drop touches down the tray entry is long gone.
    this.heldTone = tone;
    this.ghost.visible = !!piece;
    this.ghostPlaced = false;

    for (const child of [...this.ghost.children]) {
      this.ghost.remove(child);
      child.material.dispose();
    }
    if (!piece) return;

    const stones = piecePolygons(piece).map((poly) => {
      // The tray's artwork has y running down the page; flip it so the piece is
      // y-up like every frame it gets placed into. Without this it starts a
      // drag upside down and rights itself on reaching a face.
      const cx = poly.points.reduce((sum, p) => sum + p[0], 0) / poly.points.length;
      const cy = poly.points.reduce((sum, p) => sum + p[1], 0) / poly.points.length;
      return { x: cx, y: -cy, up: poly.up };
    });

    // Centre the stones on the piece, so the group's origin is its middle.
    this.ghostCentre.set(
      stones.reduce((sum, s) => sum + s.x, 0) / stones.length,
      stones.reduce((sum, s) => sum + s.y, 0) / stones.length,
    );

    for (const stone of stones) {
      const gem = new Mesh(GEM_GEOMETRY, gemMaterial());
      gem.material.color.copy(toneColor(tone));
      gem.material.emissive.copy(toneColor(tone)).multiplyScalar(0.12);
      gem.position.set(stone.x - this.ghostCentre.x, stone.y - this.ghostCentre.y, 0);
      if (!stone.up) gem.quaternion.copy(FLIP_Z);
      this.ghost.add(gem);
    }
  }

  /**
   * Aim the dragged piece.
   *
   * The piece always rides at the pointer, square on to the camera, so it stays
   * whole and legible in the hand however the board is turned. Where it would
   * land is shown by lighting the cells on the board, not by seating the piece
   * — that way nothing is hidden under the piece itself. It eases rather than
   * jumping, so it reads as being carried rather than teleported.
   */
  setGhostTarget(pointer) {
    if (!this.heldPiece || !pointer || this.drop) return;

    this.ghostTargetPos.copy(this.#pointerPoint(pointer.x, pointer.y));
    this.ghostTargetQuat
      .copy(this.pyramid.getWorldQuaternion(BOARD_TURN).invert())
      .multiply(this.camera.quaternion);
    this.ghostPointer.set(pointer.x, pointer.y);

    // First aim of a drag: appear where it belongs instead of flying in, and hang
    // straight rather than arriving mid-swing on last drag's momentum.
    if (!this.ghostPlaced) {
      this.ghostPlaced = true;
      this.ghost.position.copy(this.ghostTargetPos);
      this.ghostQuat.copy(this.ghostTargetQuat);
      this.ghost.quaternion.copy(this.ghostTargetQuat);
      this.ghostPointerLast.copy(this.ghostPointer);
      this.sway.set(0, 0);
      this.swayVel.set(0, 0);
    }
  }

  /**
   * Where the piece in hand would come to rest on `anchorId`: squared up with
   * the face and lined up on the cells it fills.
   *
   * The group's origin is the piece's centre rather than its anchor corner, so
   * the anchor cell's lattice origin has to be walked back out along the face's
   * own axes to reach it. Public because it is worth testing directly: get this
   * offset wrong and a piece lands a cell out, or subtly rotated.
   */
  seatedTransform(anchorId) {
    const cell = CELLS[anchorId];
    const basis = faceBasis(cell.face);
    const { i, j } = cellLatticeOrigin(anchorId);
    const origin = latticeVertex(cell.face, i, j);

    // y-up to match the mesh, which makes the third axis the outward normal.
    const right = new Vector3(...basis.right);
    const up = new Vector3(...basis.down).negate();
    const out = new Vector3().crossVectors(right, up);

    const position = new Vector3(...origin)
      .addScaledVector(right, this.ghostCentre.x)
      .addScaledVector(up, this.ghostCentre.y)
      .addScaledVector(out, TUNING.gemLift);

    const quaternion = new Quaternion()
      .setFromRotationMatrix(new Matrix4().makeBasis(right, up, out));

    return { position, quaternion };
  }

  /**
   * Fly the piece in hand down onto the face and leave it there.
   *
   * Its stones are withheld from the board until it lands, so the piece is
   * only ever drawn once — the thing being carried becomes the thing that is
   * placed, rather than one being swapped for the other.
   *
   * @returns {Promise<void>} resolved once the piece has settled.
   */
  dropHeldPiece(anchorId, cells) {
    if (!this.heldPiece) return Promise.resolve();

    const to = this.seatedTransform(anchorId);
    for (const id of cells) this.withheld.add(id);

    return new Promise((resolve) => {
      this.drop = {
        startedAt: null,
        cells,
        tone: this.heldTone ?? 0,
        fromPos: this.ghost.position.clone(),
        fromQuat: this.ghost.quaternion.clone(),
        toPos: to.position,
        toQuat: to.quaternion,
        resolve,
      };
    });
  }

  /** Keep drawing stones the rules have already cleared, until told otherwise. */
  holdStones(tonesByCell) {
    this.heldStones = new Map(tonesByCell);
  }

  /** Let the held stones go — they clear with the usual flare, and shatter. */
  releaseStones(now = performance.now()) {
    for (const id of this.heldStones.keys()) this.clearing.set(id, now);
    // Fired before the map is dropped: the shatter wants each stone's own
    // colour, and this is the last moment anything knows what it was.
    this.vfx?.stonesShattered(this.heldStones);
    this.heldStones = new Map();
  }

  /* ------------------------------------------------------------------- glow */

  /**
   * A glowing band tracing the outline of a set of cells.
   *
   * The outline is whichever edges are not shared: two cells that touch meet
   * along exactly one edge, so an edge seen twice is interior and an edge seen
   * once is on the border. Each border edge becomes a quad straddling it,
   * overrun by half its width at both ends so the corners close up.
   */
  addGlow(cells, { kind = 'place', width = GLOW_WIDTH } = {}) {
    if (!cells?.length) return null;
    const color = kind === 'score' ? COLORS.glowScore : COLORS.glowPlace;
    const mesh = this.#outlineMesh(cells, color, width);
    this.glows.add(mesh);
    // A scoring glow is one formation taking its turn in the announcement, so
    // the particle border goes on here rather than at the top of the sequence
    // — it lands on exactly the shape being announced, one at a time.
    if (kind === 'score') this.vfx?.formationCleared(cells);
    return mesh;
  }

  /**
   * The outline band itself: geometry, material and lift off the face.
   *
   * Shares `outlineEdges` with the particle borders rather than counting edges
   * again here, so the band and the glow that rides it cannot end up tracing
   * different shapes.
   */
  #outlineMesh(cells, color, width) {
    const normal = new Vector3(...faceNormal(CELLS[cells[0]].face));
    const positions = [];

    for (const edge of outlineEdges(cells)) {
      const A = new Vector3(...edge.a);
      const B = new Vector3(...edge.b);
      const along = new Vector3(...edge.along);
      // Symmetric about the edge, so which way `outward` faces makes no odds.
      const across = new Vector3(...edge.outward).setLength(width / 2);

      A.addScaledVector(along, -width / 2);
      B.addScaledVector(along, width / 2);

      const p1 = A.clone().sub(across);
      const p2 = B.clone().sub(across);
      const p3 = B.clone().add(across);
      const p4 = A.clone().add(across);
      positions.push(
        ...p1.toArray(), ...p2.toArray(), ...p3.toArray(),
        ...p1.toArray(), ...p3.toArray(), ...p4.toArray(),
      );
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));

    // Additive, so it reads as light thrown onto the board rather than paint.
    const mesh = new Mesh(
      geometry,
      new MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 1,
        blending: AdditiveBlending,
        depthWrite: false,
        side: DoubleSide,
      }),
    );
    mesh.position.copy(normal.clone().multiplyScalar(GLOW_LIFT));
    mesh.renderOrder = 3;
    return mesh;
  }

  /** Fireworks over a move that closed several formations at once. */
  celebrateCombo(cells, count) {
    this.vfx?.combo(cells, count);
  }

  /**
   * A completion, out of the top of the pyramid: the column of light, and the
   * sparks that ride it.
   *
   * Fired per formation as its score is announced rather than once per move, so
   * a combo throws a beam per shape and each one goes up harder than the last —
   * `ApexBeam.fire` takes the max of what is already burning, so they mount
   * rather than restarting each other.
   *
   * @param {{cells: number, combo: number}} move stones cleared, formations closed
   */
  fireApexBeam(move) {
    const shape = this.beam?.fire(move);
    if (shape) this.vfx?.apexBurst(shape.power);
    return shape;
  }

  clearGlows() {
    for (const mesh of [...this.glows.children]) {
      this.glows.remove(mesh);
      mesh.geometry.dispose();
      mesh.material.dispose();
    }
  }

  /** Abandon anything mid-animation, for a restart. */
  resetPresentation() {
    this.setPreview(null, null, null);
    this.setHeldPiece(null);
    this.clearGlows();
    this.clearing.clear();
    // Dropped rather than released: a restart is not a clear, so the stones
    // that were about to shatter simply stop existing.
    this.heldStones = new Map();
    this.withheld.clear();
    this.vfx?.reset(performance.now());
    this.beam?.reset();
  }

  /**
   * Project board-space points to screen coordinates, in one batch.
   *
   * Batched because aiming needs every candidate placement projected on every
   * pointer move, and doing them one at a time would re-read the canvas rect —
   * a layout flush — dozens of times a frame.
   *
   * @param {number[][]} points board-space `[x, y, z]`
   */
  projectBoardPoints(points) {
    const rect = this.canvas.getBoundingClientRect();
    this.pyramid.updateMatrixWorld(true);
    const v = new Vector3();

    return points.map((p) => {
      v.set(p[0], p[1], p[2]);
      this.pyramid.localToWorld(v);
      v.project(this.camera);
      return {
        x: rect.left + ((v.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - v.y) / 2) * rect.height,
      };
    });
  }

  /**
   * Roughly how large a cell on the front face is on screen right now, in
   * pixels — the natural unit for "near enough" when aiming, and it has to be
   * measured rather than assumed: it changes with the viewport and with tilt.
   */
  frontCellScreenSize() {
    const id = CELLS.findIndex((cell) => cell.face === this.frontFace);
    if (id < 0) return 0;

    const corners = this.projectBoardPoints(cellCorners(id));
    let longest = 0;
    for (let i = 0; i < corners.length; i += 1) {
      const a = corners[i];
      const b = corners[(i + 1) % corners.length];
      longest = Math.max(longest, Math.hypot(a.x - b.x, a.y - b.y));
    }
    return longest;
  }

  /** Where a set of cells sits on screen, for hanging a label off it. */
  projectCells(cells) {
    if (!cells?.length) return null;
    const centre = new Vector3();
    for (const id of cells) centre.add(new Vector3(...cellCentroid(id)));
    centre.divideScalar(cells.length);
    centre.addScaledVector(new Vector3(...faceNormal(CELLS[cells[0]].face)), GLOW_LIFT);

    this.pyramid.localToWorld(centre);
    centre.project(this.camera);

    const rect = this.canvas.getBoundingClientRect();
    return {
      x: rect.left + ((centre.x + 1) / 2) * rect.width,
      y: rect.top + ((1 - centre.y) / 2) * rect.height,
    };
  }

  /**
   * A point under the pointer, level with the pyramid, for a free-floating
   * piece. Returned in board coordinates, since that is where the ghost lives.
   */
  #pointerPoint(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);

    const toTarget = new Vector3().subVectors(TARGET, this.camera.position);
    const depth = toTarget.dot(this.raycaster.ray.direction);
    return this.pyramid.worldToLocal(this.raycaster.ray.at(depth, new Vector3()));
  }

  /* ------------------------------------------------------------------ state */

  syncFilled(filled, tones) {
    for (let id = 0; id < CELL_COUNT; id += 1) {
      this.cellState[id] = filled.has(id) ? 'filled' : 'empty';
      this.cellTone[id] = tones?.get(id) ?? 0;
    }
  }

  /**
   * The cells the drop would fill, and the already-filled cells of any
   * formation it would complete.
   *
   * `tone` is the held piece's colour: the highlight wears it, so the cells
   * light up in the colour of the stones about to land in them rather than in a
   * generic marker colour.
   */
  setPreview(cells, armed, tone = null) {
    this.preview = new Set(cells ?? []);
    this.armed = new Set((armed ?? []).filter((id) => !this.preview.has(id)));
    this.previewTone = tone;

    // Looping glow and aurora over the target. Told on every pointer move and
    // left to notice for itself that most moves do not change the target —
    // restarting the loops each frame would strobe the border.
    this.vfx?.setHover(cells?.length ? cells : null, tone ?? 0);

    // The carried piece sits over its own target — that is the point, you aim
    // by eye — so the lit cells are mostly behind it and only their fringe
    // shows. An outline fixes that: the piece is a flat billboard and the cells
    // lie on a tilted face, so the border reads all the way round it.
    //
    // Rebuilt only when the target actually changes. This runs on every pointer
    // move, and most moves leave the target where it was.
    const key = cells?.length ? `${tone}:${[...cells].sort((a, b) => a - b).join(',')}` : '';
    if (key === this.previewKey) return;
    this.previewKey = key;

    for (const mesh of [...this.previewGlows.children]) {
      this.previewGlows.remove(mesh);
      mesh.geometry.dispose();
      mesh.material.dispose();
    }
    if (!key) return;

    this.previewGlows.add(
      this.#outlineMesh(cells, toneColor(tone), GLOW_WIDTH * PREVIEW_GLOW_WIDTH),
    );
  }

  /* ------------------------------------------------------------------ camera */

  /**
   * The face turned most directly toward the viewer.
   *
   * Read off the geometry rather than inferred from the turn angle: the board
   * rotates while the camera stays put, so the mapping from angle to face runs
   * backwards from what an orbiting camera would give, and getting it from the
   * normals means it cannot drift out of step with what is actually on screen.
   */
  get facingFace() {
    return this.frontFace;
  }

  /** Take hold of the pyramid: kill any coast or page in progress. */
  beginOrbit() {
    this.dragging = true;
    this.spin = 0;
    this.page = null;
    this.flickSpeed = 0;
    this.flickAt = performance.now();
  }

  /** Free orbit: the turn is unbounded, the tilt is clamped. */
  orbitBy(dx, dy) {
    const now = performance.now();
    const dt = Math.max(8, now - this.flickAt) / 1000;
    this.flickAt = now;

    // Dragging right turns the board to the right. The camera is fixed and the
    // board spins, so this runs opposite to an orbiting camera.
    const turn = dx;
    this.azimuth += turn;
    this.elevation = clamp(this.elevation + dy, MIN_ELEVATION, MAX_ELEVATION);

    // Smoothed so one jittery event cannot fake a flick.
    this.flickSpeed = this.flickSpeed * 0.6 + (turn / dt) * 0.4;
  }

  /**
   * Let go. A flick pages to the next face and settles there; a gentler
   * release coasts to a stop wherever it lands; a slow one just stops.
   */
  endOrbit() {
    this.dragging = false;

    const stale = performance.now() - this.flickAt > FLICK_WINDOW_MS;
    const speed = stale ? 0 : this.flickSpeed;

    if (Math.abs(speed) >= FLING_SPEED) {
      this.#pageTo(Math.sign(speed));
    } else if (Math.abs(speed) >= COAST_SPEED) {
      this.spin = speed;
    } else {
      this.spin = 0;
    }
  }

  /** Ease to the next face in `direction`, exactly one across. */
  #pageTo(direction) {
    const steps = this.azimuth / SNAP_STEP;
    const to =
      (direction > 0 ? Math.floor(steps + 1e-4) + 1 : Math.ceil(steps - 1e-4) - 1) * SNAP_STEP;
    this.spin = 0;
    this.page = { from: this.azimuth, to, start: performance.now() };
  }

  /**
   * Turn to put a chosen face front and centre. Spinning the board means face
   * `f` comes forward at azimuth `-f * 120°`, so the sign runs opposite to the
   * face index; take whichever multiple of a full turn is nearest.
   *
   * No longer reachable by a player — the arrow keys that used to call it are gone,
   * and turning the pyramid is a drag. It stays because the smoke test uses it to
   * settle the view on a known face before measuring anything, which is worth more
   * than the handful of lines it costs.
   */
  focusFace(face) {
    const desired = -face * SNAP_STEP;
    const full = 2 * Math.PI;
    const to = desired + Math.round((this.azimuth - desired) / full) * full;

    this.spin = 0;
    this.page = { from: this.azimuth, to, start: performance.now() };
  }

  #advanceRotation(now, dt) {
    if (this.dragging) return;

    if (this.page) {
      const t = clamp((now - this.page.start) / PAGE_MS, 0, 1);
      this.azimuth = this.page.from + (this.page.to - this.page.from) * easeOutCubic(t);
      if (t >= 1) this.page = null;
      return;
    }

    if (this.spin) {
      this.azimuth += this.spin * dt;
      this.spin *= COAST_FRICTION ** dt;
      if (Math.abs(this.spin) < 0.02) this.spin = 0;
    }
  }

  /* ----------------------------------------------------------------- picking */

  /** Where the camera sits relative to the board, now that the board turns. */
  #cameraInBoardSpace() {
    return this.pyramid.worldToLocal(EYE.copy(this.camera.position));
  }

  /* ------------------------------------------------------------------ frame */

  resize() {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);

    this.viewW = w;
    this.viewH = h;
    // The impact ring measures its radius in screen widths, so it needs telling.
    this.beam?.resize(w, h);

    // Clip-space half-extents available to the pyramid.
    //
    // The board is centred on the **viewport**, not on the gap between the HUD and
    // the tray. Those two reserves are nothing like equal — the tray is more than
    // twice the HUD — so centring on the gap put the pyramid a good 50px above the
    // middle of the screen, which is what this fixes.
    //
    // Staying clear of both then costs the *larger* reserve at each end, not each
    // reserve at its own end: a band centred on h/2 cannot take more room from the
    // top than from the bottom without moving off centre again. So the pyramid is
    // drawn slightly smaller than it used to be. That is the price of the centring
    // and it is the whole of the price — nothing else here changes.
    const reserve = Math.max(HUD_PX, TRAY_PX);
    const band = Math.max(140, h - 2 * reserve);
    this.frameCentreY = h / 2;
    this.halfX = FIT_FILL;
    this.halfY = (band / h) * FIT_FILL;

    this.camera.fov = BASE_FOV;
    this.camera.aspect = w / h;
    this.#frameCamera();

    // The tray camera works straight in pixels, y up from the bottom.
    this.trayCamera.left = 0;
    this.trayCamera.right = w;
    this.trayCamera.top = h;
    this.trayCamera.bottom = 0;
    this.trayCamera.updateProjectionMatrix();
  }

  /**
   * Park the camera, once.
   *
   * It sits level with the middle of the pyramid, looking straight at it, and
   * stays there for good: turning the view spins the pyramid, so there is
   * nothing for the camera to follow. The distance and pan are sized to the box
   * the pyramid sweeps out across every orientation it can be turned to, which
   * is all "enough room to spin freely" needs to mean. Two passes converge:
   * screen extent goes as 1/distance.
   *
   * The view offset at the end is what actually centres it, and it centres the
   * box swept *over the turn at the rest tilt* rather than the board's current
   * silhouette — so the pyramid holds still on the viewport centre as it turns,
   * instead of sliding about as first one face and then another becomes the
   * widest thing on screen. The full tilt range is deliberately left out of the
   * centring (though not the fitting): see the comment at the call.
   */
  #frameCamera() {
    const { viewW: w, viewH: h } = this;
    const probe = new Vector3();
    const spun = new Vector3();

    const place = (distance) => {
      this.camera.position.set(TARGET.x, TARGET.y, TARGET.z + distance);
      this.camera.lookAt(TARGET);
      this.camera.updateProjectionMatrix();
      this.camera.updateMatrixWorld();
    };

    /**
     * The screen box swept by the pyramid over every turn, at the elevations
     * given. Fitting passes the full tilt range; centring passes only the rest
     * tilt — see the call sites.
     */
    const sweptBox = (elevations) => {
      const box = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
      const turn = new Quaternion();
      const tilt = new Quaternion();
      const axisX = new Vector3(1, 0, 0);
      const axisY = new Vector3(0, 1, 0);

      for (const elevation of elevations) {
        tilt.setFromAxisAngle(axisX, elevation);
        // The silhouette repeats every 120 degrees, so one period covers it.
        for (let a = 0; a < 12; a += 1) {
          turn.setFromAxisAngle(axisY, (a * SNAP_STEP) / 12);
          for (const corner of SILHOUETTE_CORNERS) {
            spun.copy(corner).sub(TARGET).applyQuaternion(turn).applyQuaternion(tilt).add(TARGET);
            probe.copy(spun).project(this.camera);
            box.minX = Math.min(box.minX, probe.x);
            box.maxX = Math.max(box.maxX, probe.x);
            box.minY = Math.min(box.minY, probe.y);
            box.maxY = Math.max(box.maxY, probe.y);
          }
        }
      }
      return box;
    };

    /** Every tilt the view can reach, for the fitting passes. */
    const allTilts = Array.from(
      { length: 7 },
      (_, e) => MIN_ELEVATION + ((MAX_ELEVATION - MIN_ELEVATION) * e) / 6,
    );

    this.camera.clearViewOffset();
    this.distance = 12;
    place(this.distance);

    for (let pass = 0; pass < 3; pass += 1) {
      const box = sweptBox(allTilts);
      this.distance = clamp(
        this.distance *
          Math.max(
            (box.maxX - box.minX) / (2 * this.halfX),
            (box.maxY - box.minY) / (2 * this.halfY),
          ),
        2,
        400,
      );
      place(this.distance);
    }

    // Centred on the box swept at the *rest* tilt, not the whole tilt range.
    // The union over every elevation reaches much further below the resting
    // silhouette than above it — tilting the board back drops its base corners
    // a long way down the frame — so centring the union parked the pyramid
    // visibly high in the viewport whenever it was actually at rest, which is
    // nearly always. Azimuth stays fully swept: the board turns constantly, and
    // this is what keeps it from sliding about as it does.
    const box = sweptBox([DEFAULT_ELEVATION]);
    const centreY = ((1 - box.maxY) / 2 + (1 - box.minY) / 2) * (h / 2);
    const centreX = ((box.minX + 1) / 2 + (box.maxX + 1) / 2) * (w / 2);
    this.camera.setViewOffset(w, h, centreX - w / 2, centreY - this.frameCentreY, w, h);
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld();
  }

  /** Spin and tilt the pyramid to the current view angles. */
  #orientPyramid() {
    this.pivot.quaternion
      .setFromAxisAngle(TILT_AXIS, this.elevation)
      .multiply(SPIN.setFromAxisAngle(SPIN_AXIS, this.azimuth + SNAP_YAW));
    this.pivot.updateMatrixWorld(true);
  }

  render(now = performance.now()) {
    const dt = clamp((now - this.lastFrame) / 1000, 0, 0.05) || 0.016;
    this.lastFrame = now;

    this.#advanceRotation(now, dt);
    this.#orientPyramid();

    this.#updateFacing();
    this.#updateDrop(now);
    this.#updateCells(now);
    this.#updateGhost(dt);

    // After the cell pass, which is what decides whether a stone is showing at
    // all this frame, and before the draw.
    this.vfx?.update(dt, now, (id) => this.gemMeshes[id].visible && this.visibleFaces[CELLS[id].face]);

    // After the turn and tilt above, so the beam's billboard yaw and the impact
    // ring's aim both see where the apex is *this* frame. The column itself
    // rides the pyramid group and needs no walking.
    this.beam?.update(dt, this.camera);

    // The piece in hand rides a mirror of the board's turn, so its pass lines
    // up with the board's. Kept in step every frame rather than only when the
    // ghost is visible: its pass renders regardless, and a stale pivot would
    // show as the piece snapping into place at the start of a drag.
    this.ghostPivot.quaternion.copy(this.pivot.quaternion);
    this.ghostPivot.position.copy(this.pivot.position);

    if (this.trayRoot.children.length) {
      this.trayTime = (this.trayTime ?? 0) + dt;
      for (const group of this.trayRoot.children) {
        group.rotation.y = Math.sin(this.trayTime * 0.6) * 0.22;
      }
    }

    // One composited, bloomed draw — the layering that used to be three
    // `render` calls over a cleared depth buffer now lives in the node graph
    // built by `#buildPostProcessing`. An empty ghost or tray scene simply
    // contributes nothing.
    this.post.render();
  }

  #updateGhost(dt) {
    // A drop drives the ghost on its own clock; chasing the pointer as well
    // would fight it.
    if (!this.ghost.visible || this.drop) return;
    const k = smoothing(dt, GHOST_RATE);
    this.ghost.position.lerp(this.ghostTargetPos, k);
    this.ghostQuat.slerp(this.ghostTargetQuat, k);
    this.#updateSway(dt);
  }

  /**
   * Swing the piece under the pointer.
   *
   * Pointer speed sets the angle the piece would hang at if it were dragged at that
   * speed for ever; a damped spring then chases that angle. Both signs are the way
   * a hanging thing behaves rather than the way a steering wheel does: drag right
   * and the piece trails to the *left* of the pointer, which is why the roll is
   * negated.
   *
   * Applied by post-multiplication, so the swing happens about the piece's own axes
   * — which the base rotation has already squared up with the camera. That keeps the
   * sway on screen axes however the board underneath is turned, and it composes with
   * the ease instead of fighting it.
   */
  #updateSway(dt) {
    if (dt <= 0) return;

    // Screen pixels per second, differenced over the frame.
    const vx = (this.ghostPointer.x - this.ghostPointerLast.x) / dt;
    const vy = (this.ghostPointer.y - this.ghostPointerLast.y) / dt;
    this.ghostPointerLast.copy(this.ghostPointer);

    const restRoll = clamp(-vx * SWAY_DRIVE, -SWAY_MAX, SWAY_MAX);
    const restPitch = clamp(-vy * SWAY_DRIVE * SWAY_PITCH_SHARE, -SWAY_MAX, SWAY_MAX);

    // Damped spring toward that rest angle. Gravity is the restoring term, so this
    // is a pendulum and not an easing curve — it goes through level and comes back.
    this.swayVel.x += (-SWAY_STIFFNESS * (this.sway.x - restRoll) - SWAY_DAMPING * this.swayVel.x) * dt;
    this.swayVel.y += (-SWAY_STIFFNESS * (this.sway.y - restPitch) - SWAY_DAMPING * this.swayVel.y) * dt;
    this.sway.x += this.swayVel.x * dt;
    this.sway.y += this.swayVel.y * dt;

    // Rebuilt from the eased base each frame, never accumulated onto itself.
    this.ghost.quaternion
      .copy(this.ghostQuat)
      .multiply(
        SWAY_ROLL.setFromAxisAngle(SWAY_AXIS_Z, this.sway.x).multiply(
          SWAY_PITCH.setFromAxisAngle(SWAY_AXIS_X, this.sway.y),
        ),
      );
  }

  /** Fly a dropped piece onto its face, then hand the cells to the board. */
  #updateDrop(now) {
    const drop = this.drop;
    if (!drop) return;

    // Teardown waits a frame. The caller reveals the stones on the board in
    // response to the promise, so retiring the ghost in the same frame would
    // leave one where neither is drawn. Held over, the two coincide exactly —
    // the ghost is sitting where the stones have just appeared.
    if (drop.done) {
      for (const id of drop.cells) this.withheld.delete(id);
      this.drop = null;
      this.ghost.visible = false;
      this.heldPiece = null;
      return;
    }

    if (drop.startedAt === null) drop.startedAt = now;

    const t = Math.min(1, (now - drop.startedAt) / DROP_MS);
    const eased = easeOutCubic(t);
    this.ghost.position.lerpVectors(drop.fromPos, drop.toPos, eased);
    this.ghost.quaternion.slerpQuaternions(drop.fromQuat, drop.toQuat, eased);

    if (t >= 1) {
      drop.done = true;
      // The stones touch down here, a frame before the ghost is retired and
      // the board takes them over, so this is where the dust belongs.
      this.vfx?.pieceLanded(drop.cells, drop.tone);
      drop.resolve();
    }
  }

  /**
   * Hide the faces pointing away from the camera, along with their wireframe.
   * A tetrahedron is convex, so a face is edge-on at the moment it flips and
   * the switch is invisible.
   */
  #updateFacing() {
    const eye = this.#cameraInBoardSpace();

    const visible = [];
    let bestFace = this.frontFace ?? 0;
    let bestFacing = -Infinity;

    for (let face = 0; face < FACE_COUNT; face += 1) {
      const n = faceNormal(face);
      const c = this.faceCentroids[face];
      const to = [eye.x - c[0], eye.y - c[1], eye.z - c[2]];
      const len = Math.hypot(to[0], to[1], to[2]) || 1;
      const facing = (to[0] * n[0] + to[1] * n[1] + to[2] * n[2]) / len;

      visible[face] = facing > 0;
      this.faceEdges[face].visible = visible[face];

      if (facing > bestFacing) {
        bestFacing = facing;
        bestFace = face;
      }
    }
    this.frontFace = bestFace;

    for (let id = 0; id < CELL_COUNT; id += 1) {
      this.cellMeshes[id].visible = visible[CELLS[id].face];
    }

    // A seam is built once, on the crease, so it stays up while either of the
    // faces meeting there is turned toward us.
    for (const wire of this.seamEdges) {
      wire.visible = wire.userData.faces.some((face) => visible[face]);
    }

    this.visibleFaces = visible;
  }

  #updateCells(now) {
    const focused = this.facingFace;

    for (const mesh of this.previewGlows.children) {
      mesh.material.opacity = 1 - PREVIEW_GLOW_PULSE + Math.sin(now / 130) * PREVIEW_GLOW_PULSE;
    }

    for (let id = 0; id < CELL_COUNT; id += 1) {
      const mesh = this.cellMeshes[id];
      const material = mesh.material;
      const centroid = mesh.userData.centroid;
      const n = mesh.userData.normal;
      const face = CELLS[id].face;

      let color = FACE_TINTS[face];
      let emissive = 0;
      let lift = TUNING.liftBase;
      let scale = 1;
      let dim = face === focused ? 1 : UNFOCUSED_DIM;

      const startedAt = this.clearing.get(id);
      let flaring = false;
      if (startedAt !== undefined) {
        const t = (now - startedAt) / CLEAR_MS;
        if (t >= 1) {
          this.clearing.delete(id);
        } else {
          flaring = true;
          color = COLORS.clearing;
          dim = 1;
          emissive = (1 - t) * 1.6;
          scale = 1 + Math.sin(Math.PI * t) * 0.35;
          lift = TUNING.liftBase + Math.sin(Math.PI * t) * 0.2;
        }
      }

      // The stone in this cell, if any. Held stones are ones the rules have
      // already cleared but the scoring sequence has yet to show going;
      // withheld ones are still in the air, on a piece flying down to land.
      const held = this.heldStones.get(id);
      const occupied =
        (this.cellState[id] === 'filled' || held !== undefined) && !this.withheld.has(id);

      const gem = this.gemMeshes[id];
      const stoneUp = occupied || flaring;
      gem.visible = stoneUp;

      if (stoneUp) {
        const armed = this.armed.has(id);
        const stone = toneColor(held ?? this.cellTone[id]);
        gem.material.color
          .copy(flaring ? COLORS.clearing : stone)
          .multiplyScalar(face === focused ? 1 : 0.86);
        gem.material.emissive
          .copy(flaring ? COLORS.clearing : stone)
          .multiplyScalar(flaring ? emissive : armed ? 0.3 + Math.sin(now / 110) * 0.14 : 0.05);
        gem.scale.setScalar(flaring ? scale : 1);
      }

      // Where the piece in hand would land. The piece itself rides at the
      // pointer rather than seating itself, so this highlight is the only
      // thing saying where it goes — and it is not hidden under the piece.
      if (!flaring && this.preview.has(id)) {
        // In the held piece's own colour, so the cell shows what is coming.
        color = this.previewTone === null ? COLORS.preview : toneColor(this.previewTone);
        dim = 1;
        emissive = 0.62 + Math.sin(now / 130) * 0.16;
        lift = TUNING.liftBase + TUNING.liftPreview;
        scale = 1.05;
      } else if (!flaring && occupied) {
        // A filled socket goes dark: the gem covers it. Armed cells are always
        // filled ones — a formation the drop completes is preview plus filled,
        // and setPreview takes the preview cells out of the armed set — so
        // their highlight is the pulse on the stone above, not the socket.
        color = COLORS.shell;
        dim = 1;
        emissive = 0;
        lift = TUNING.liftBase + TUNING.liftFilled;
      }

      material.color.copy(color).multiplyScalar(dim);
      material.emissive.copy(color).multiplyScalar(emissive);
      mesh.scale.setScalar(flaring ? 1 : scale);
      mesh.position.set(
        centroid[0] + n[0] * lift,
        centroid[1] + n[1] * lift,
        centroid[2] + n[2] * lift,
      );
    }
  }
}
